import { useCallback, useEffect, useMemo } from 'react';
import { AnimationConfig } from '@react-spring/core';
import { useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';

import { useLogger } from '@mopla/logger';

import { SnapPoint } from '../SnapPoint';

/** Velocity threshold for fast swipes (measured: slow ~0.9, fast ~3.6) */
const VELOCITY_THRESHOLD = 1; // Some sweet spot between the slow and fast swipes

/** Spring physics configuration */
const DEFAULT_SPRING_CONFIG = {
	tension: 300,
	friction: 30,
};

export type TSpringConfig = Pick<
	AnimationConfig,
	'tension' | 'friction' | 'frequency' | 'damping'
>;

export interface IUseBottomSheetSnapParams {
	/** Snap points, e.g. [SnapPoint.vh(0.3), SnapPoint.px(400)]. SnapPoint needs to memoized. */
	snapPoints: SnapPoint[];
	/** Current snap index */
	currentSnapIndex: number;
	/** Callback when snap index changes */
	onSnapIndexChange: (index: number) => void;
	/** Whether to animate from bottom (0vh) to initialSnapIndex on mount. Default: true */
	animateOnMount?: boolean;
	/** Whether the bottom sheet is open */
	open: boolean;
	/** Callback called when close animation completes */
	onCloseAnimationComplete?: () => void;
	/** Spring animation config. Defaults to { tension: 300, friction: 30 } */
	animationConfig?: Partial<TSpringConfig>;
}

export const useBottomSheetSnap = ({
	snapPoints,
	currentSnapIndex,
	onSnapIndexChange,
	animateOnMount = true,
	open,
	onCloseAnimationComplete,
	animationConfig,
}: IUseBottomSheetSnapParams) => {
	const logger = useLogger('MplSnappingBottomSheet');
	const viewportHeight = window.innerHeight;

	const springConfig = useMemo(
		() => ({ ...DEFAULT_SPRING_CONFIG, ...animationConfig }),
		[animationConfig]
	);

	/** Validate snap points */
	useEffect(() => {
		if (snapPoints.length === 0) {
			logger.warn('snapPoints cannot be empty');
		}
		if (snapPoints.some((p) => p.toFraction() < 0 || p.toFraction() > 1)) {
			logger.warn('snap-point fractions must be between 0 and 1');
		}
	}, [snapPoints, logger]);

	/** Get snap point at a clamped index */
	const getSnapPoint = useCallback(
		(index: number): SnapPoint => {
			const clampedIndex = Math.max(0, Math.min(index, snapPoints.length - 1));
			return snapPoints[clampedIndex];
		},
		[snapPoints]
	);

	/** Calculate target snap index based on height, velocity and direction */
	const calculateTargetSnapIndex = (
		currentHeight: string,
		velocityY: number,
		directionY: number
	): number => {
		const currentPercentage =
			SnapPoint.fromVhString(currentHeight).toFraction();
		/** velocityY is always positive (magnitude), directionY indicates direction (-1, 0, 1) */

		/** Fast swipe detection */
		if (velocityY > VELOCITY_THRESHOLD) {
			if (directionY < 0) {
				/** Swipe UP (directionY = -1) → expand (increase height, increase index) */
				return Math.min(currentSnapIndex + 1, snapPoints.length - 1);
			} else if (directionY > 0) {
				/** Swipe DOWN (directionY = 1) → collapse (decrease height, decrease index) */
				return Math.max(currentSnapIndex - 1, 0);
			}
		}

		/** Slow drag → find nearest snap point */
		let nearestIndex = 0;
		let minDistance = Infinity;

		snapPoints.forEach((snapPoint, index) => {
			const distance = Math.abs(snapPoint.toFraction() - currentPercentage);
			if (distance < minDistance) {
				minDistance = distance;
				nearestIndex = index;
			}
		});

		/** Always snap to nearest (no minimum threshold) */
		return nearestIndex;
	};

	/** Initialize spring - start from 0vh if animateOnMount is true */
	const [springs, api] = useSpring(() => ({
		height: animateOnMount
			? '0vh'
			: getSnapPoint(currentSnapIndex).toVhString(),
		config: springConfig,
	}));

	/** Animate height based on open state and snap index changes */
	useEffect(() => {
		if (!open) {
			api.start({
				height: '0vh',
				onRest: (result) => {
					/** Only call if animation finished naturally — not if it was cancelled by a re-open */
					if (result.finished) {
						onCloseAnimationComplete?.();
					}
				},
			});
			return;
		}

		api.start({
			height: getSnapPoint(currentSnapIndex).toVhString(),
		});
	}, [open, currentSnapIndex, api, getSnapPoint, onCloseAnimationComplete]);

	/** Handle window resize / orientation change */
	useEffect(() => {
		const handleResize = () => {
			/** Re-apply current snap — important for px-based points whose vh fraction changes on resize */
			api.start({
				height: getSnapPoint(currentSnapIndex).toVhString(),
				immediate: true,
			});
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [api, currentSnapIndex, getSnapPoint]);

	/** Gesture handler */
	const bind = useDrag(
		({
			down,
			movement: [, my],
			velocity: [, vy],
			direction: [, dy],
			last,
			memo,
		}) => {
			/** Initialize memo with starting height */
			if (!memo) {
				memo = springs.height.get();
			}

			if (down) {
				/** During drag: update height with movement */
				/** For bottom drawer: drag down (positive my) should DECREASE height (pull down handle) */
				/** So we need to INVERT my */
				const startHeightVh = SnapPoint.fromVhString(memo).toFraction() * 100; // Current height in vh
				const movementVh = (-my / viewportHeight) * 100; // Movement in vh (inverted!)
				const newHeightVh = startHeightVh + movementVh;

				/** Clamp between min and max snap heights */
				const minHeightVh = getSnapPoint(0).toFraction() * 100;
				const maxHeightVh =
					getSnapPoint(snapPoints.length - 1).toFraction() * 100;
				const clampedHeightVh = Math.max(
					minHeightVh,
					Math.min(maxHeightVh, newHeightVh)
				);

				api.start({
					height: `${clampedHeightVh}vh`,
					immediate: true,
				});
			} else if (last) {
				/** On release: snap to target */
				const currentHeight = springs.height.get();
				const targetIndex = calculateTargetSnapIndex(currentHeight, vy, dy);

				if (targetIndex !== currentSnapIndex) {
					onSnapIndexChange(targetIndex);
				} else {
					/** Snap back to current index if no change */
					api.start({
						height: getSnapPoint(currentSnapIndex).toVhString(),
					});
				}
			}

			return memo;
		},
		{
			axis: 'y',
			pointer: { touch: true },
		}
	);

	return {
		springs,
		bind,
	};
};
